import './style.scss'

import { HDSProgressIndicator } from '@here/hds-react-components'
import classNames from 'classnames'
import React, { useEffect, useRef } from 'react'

function handleScroll(event: React.UIEvent<HTMLElement>): void {
  const element = event.target as HTMLElement
  element.classList.toggle('ScrollableContainer--scrolled', element.scrollTop > 1)
}

interface ScrollableContainerProps {
  className?: string
  children?: JSX.Element | null | false | undefined
  onScrollEnd?: (iteration: number) => void
  loading?: boolean
  omitIterations?: number
}

export function ScrollableContainer(props: ScrollableContainerProps): JSX.Element {
  const { children, className, onScrollEnd, loading } = props
  const loader = useRef<HTMLDivElement>(null)
  const parent = useRef<HTMLDivElement>(null)
  const iterationRef = useRef<number>(0)
  const scrollEndRef = useRef<((iteration: number) => void) | undefined>(undefined)

  // Create this ref to protect the observer from being recreated
  // every time a new function instance is passed to onScrollEnd
  useEffect(() => {
    scrollEndRef.current = onScrollEnd
  }, [onScrollEnd])

  useEffect(() => {
    const currentRef = loader.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        const omitIterations = props.omitIterations ?? -1
        if (entry.isIntersecting && iterationRef.current > omitIterations) {
          scrollEndRef.current && scrollEndRef.current(iterationRef.current)
        } else if (entry.isIntersecting) {
          iterationRef.current = iterationRef.current + 1
        }
      },
      {
        root: parent.current,
        rootMargin: '20px',
        threshold: 1,
      }
    )
    if (currentRef) {
      observer.observe(currentRef)
    }
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [props.omitIterations])

  return (
    <div className="ScrollableContainer__wrapper">
      <div
        ref={parent}
        className={classNames('ScrollableContainer', className)}
        onScroll={handleScroll}
      >
        <>
          {children}
          {loading !== undefined && <div ref={loader} />}
        </>
      </div>
      {loading !== undefined && loading && (
        <HDSProgressIndicator
          className="ScrollableContainer__loader"
          color="accent"
          size="small"
          variant="indeterminate"
          indicatorType="circular"
        />
      )}
    </div>
  )
}
